import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  ListItemButton,
  Typography,
} from "@mui/material";
import {
  Dashboard,
  Assignment,
  Payment,
  Flag,
  QuestionAnswer,
  Description,
  List as ListIcon,
  Business,
  Home,
  People,
  CloudUpload,
  ExitToApp,
  AttachMoney,
  CardMembership,
  Receipt,
} from "@mui/icons-material";
import { usePostgresViewer } from "components/viewer/context";
import Development from "components/development";
import { isSidebarLinkActive } from "./is-sidebar-link-active";

const sx = {
  logo: {
    width: 52,
    height: 58,
    backgroundImage: "url(https://s3.amazonaws.com/hc-images2/haven_logo_light.png)",
    backgroundSize: "237px 58px",
    display: "inline-block",
    mb: "1em",
  },
  sidebar: {
    backgroundColor: "#32445A",
    minHeight: "100vh",
    padding: "1em",
  },
  username: {
    color: "whitesmoke",
    textAlign: "center" as const,
    display: "block",
    mt: "1em",
  },
  divider: { backgroundColor: "rgba(255,255,255,0.1)" },
  item: { color: "white" },
  itemIcon: { color: "white", minWidth: "40px" },
};

type NavLink = {
  href: string;
  label: string;
  icon: React.ComponentType;
};

const NAV_LINKS: NavLink[] = [
  { href: "/dash/admin", label: "Overview", icon: Dashboard },
  { href: "/dash/admin/interests", label: "Applications", icon: Assignment },
  { href: "/dash/admin/billing", label: "Billing", icon: Payment },
  { href: "/dash/admin/feature-flags", label: "Feature Flags", icon: Flag },
  { href: "/dash/admin/form-questions", label: "Form Questions", icon: QuestionAnswer },
  { href: "/dash/admin/forms", label: "Form Drafts", icon: Description },
  { href: "/dash/admin/lists", label: "Lists", icon: ListIcon },
  { href: "/dash/admin/organizations", label: "Organizations", icon: Business },
  { href: "/dash/admin/plans", label: "Plans", icon: CardMembership },
  { href: "/dash/admin/pricing", label: "Pricing", icon: AttachMoney },
  { href: "/dash/admin/subscriptions", label: "Subscriptions", icon: Receipt },
  { href: "/dash/admin/properties", label: "Properties", icon: Home },
  { href: "/dash/admin/contracts", label: "Contracts", icon: Description },
  { href: "/dash/admin/users", label: "Users", icon: People },
  { href: "/dash/admin/importers/lottery", label: "Lottery Importer", icon: CloudUpload },
  { href: "/dash/admin/importers/waitlist", label: "Waitlist Importer", icon: CloudUpload },
];

type SideBarLinkProps = {
  href: string;
  icon: React.ComponentType;
  selected?: boolean;
  children: React.ReactNode;
};

const SideBarLink: React.FC<SideBarLinkProps> = ({ href, icon: Icon, selected, children }) => (
  <ListItemButton href={href} selected={selected} sx={sx.item}>
    <ListItemIcon sx={sx.itemIcon}>
      <Icon />
    </ListItemIcon>
    <ListItemText primary={children} slotProps={{ primary: { sx: sx.item } }} />
  </ListItemButton>
);

const SideBar: React.FC = () => {
  const viewer = usePostgresViewer();
  const location = useLocation();

  if (!viewer) return <p>Server Error fetching Viewer</p>;

  return (
    <List sx={sx.sidebar} role="navigation">
      <ListItem>
        <Link to="/dash/admin" className="text-center">
          <Box component="span" sx={sx.logo} />
        </Link>
      </ListItem>

      <Divider sx={sx.divider} />

      {NAV_LINKS.map(({ href, label, icon }) => (
        <SideBarLink
          key={href}
          href={href}
          icon={icon}
          selected={isSidebarLinkActive(location.pathname, href)}
        >
          {label}
        </SideBarLink>
      ))}

      <Development>
        <Divider sx={{ ...sx.divider, mt: "1em" }} />
        <SideBarLink href="/dash/admin/debug" icon={CloudUpload}>Debug</SideBarLink>
      </Development>

      <Divider sx={{ ...sx.divider, mt: "1em" }} />

      <SideBarLink href="/logout" icon={ExitToApp}>Log Out</SideBarLink>

      <Typography component="small" variant="caption" sx={sx.username}>
        {viewer.logins[0].username}
      </Typography>
    </List>
  );
};

export const isSidebarPathActive = isSidebarLinkActive;
export default SideBar;
